import type {
    ActionRisk,
    AiAction,
    AiActionStatus,
    AiActionType,
    AiAgentPolicy,
    AiAgentStatus,
    AiAuthorityTier,
    AiPermission,
} from '@/types/ai-authority';
import { ACTION_RISK_MAP, DEFAULT_AI_POLICY, TIER_DEFAULT_PERMISSIONS } from '@/types/ai-authority';
import { create } from 'zustand';

/* ================================================================== */
/*  AI Agent Store                                                    */
/*                                                                    */
/*  Runtime state for the autonomous AI agent.                        */
/*  Tracks: authority tier, policy, action queue, execution history,  */
/*  approval pipeline, rate limits, kill switch.                      */
/*                                                                    */
/*  The AI is a "virtual member" inside the org — every action goes   */
/*  through the same permission + audit path as a human engineer.     */
/* ================================================================== */

interface AiAgentState {
  /* -- Agent status ------------------------------------------------ */
  status: AiAgentStatus;
  policy: AiAgentPolicy;

  /* -- Action queues ----------------------------------------------- */
  /** Actions waiting for approval */
  pendingActions: AiAction[];
  /** Currently executing actions */
  executingActions: AiAction[];
  /** Completed action history (last 200) */
  actionHistory: AiAction[];

  /* -- Rate tracking ----------------------------------------------- */
  actionsThisHour: number;
  hourResetAt: string;
  spentToday: number;
  dayResetAt: string;

  /* -- UI state ---------------------------------------------------- */
  selectedActionId: string | null;
  filterRisk: ActionRisk | 'all';
  filterStatus: AiActionStatus | 'all';

  /* -- Actions: Agent lifecycle ------------------------------------ */
  setPolicy: (policy: AiAgentPolicy) => void;
  updatePolicy: (patch: Partial<AiAgentPolicy>) => void;
  setTier: (tier: AiAuthorityTier) => void;
  engageKillSwitch: () => void;
  disengageKillSwitch: () => void;

  /* -- Actions: Permission management ------------------------------ */
  getEffectivePermissions: () => AiPermission[];
  hasPermission: (permission: AiPermission) => boolean;
  canExecuteAction: (actionType: AiActionType) => {
    allowed: boolean;
    reason?: string;
  };

  /* -- Actions: Action pipeline ------------------------------------ */
  proposeAction: (
    action: Omit<AiAction, 'id' | 'status' | 'proposedAt' | 'risk' | 'autoApproved'>,
  ) => AiAction;
  approveAction: (actionId: string, approvedBy: string, note?: string) => void;
  rejectAction: (actionId: string, note?: string) => void;
  startExecution: (actionId: string) => void;
  completeAction: (actionId: string, result: AiAction['result']) => void;
  failAction: (actionId: string, errors: string[]) => void;
  rollbackAction: (actionId: string) => void;
  expireAction: (actionId: string) => void;

  /* -- Actions: UI ------------------------------------------------- */
  selectAction: (id: string | null) => void;
  setFilterRisk: (risk: ActionRisk | 'all') => void;
  setFilterStatus: (status: AiActionStatus | 'all') => void;

  /* -- Computed ---------------------------------------------------- */
  getPendingCount: () => number;
  getActionById: (id: string) => AiAction | undefined;
  isRateLimited: () => boolean;
  isBudgetExceeded: () => boolean;
}

let actionCounter = 0;
function nextActionId(): string {
  return `ai-act-${Date.now()}-${++actionCounter}`;
}

function computeEffectivePermissions(policy: AiAgentPolicy): AiPermission[] {
  const base = new Set(TIER_DEFAULT_PERMISSIONS[policy.tier]);

  // Apply overrides
  if (policy.permissionOverrides?.grant) {
    for (const p of policy.permissionOverrides.grant) base.add(p);
  }
  if (policy.permissionOverrides?.deny) {
    for (const p of policy.permissionOverrides.deny) base.delete(p);
  }

  return Array.from(base);
}

function actionNeedsApproval(
  risk: ActionRisk,
  policy: AiAgentPolicy,
  tier: AiAuthorityTier,
): boolean {
  // Kill switch → everything needs approval (effectively blocked)
  if (policy.killSwitch) return true;

  // Tier 1 → everything needs approval
  if (tier === 1) return true;

  // Policy-level requirement
  if (policy.requireApprovalFor.includes(risk)) return true;

  // Tier 2 → deploy actions always need approval
  if (tier === 2 && risk !== 'low') return true;

  return false;
}

export const useAiAgentStore = create<AiAgentState>((set, get) => ({
  // Initial state
  status: {
    isActive: true,
    tier: 1,
    permissions: TIER_DEFAULT_PERMISSIONS[1],
    actionsThisHour: 0,
    spentToday: 0,
    killSwitchEngaged: false,
    currentAction: null,
    lastHeartbeat: new Date().toISOString(),
  },

  policy: { ...DEFAULT_AI_POLICY },

  pendingActions: [],
  executingActions: [],
  actionHistory: [],

  actionsThisHour: 0,
  hourResetAt: new Date(Date.now() + 3600_000).toISOString(),
  spentToday: 0,
  dayResetAt: new Date(new Date().setHours(24, 0, 0, 0)).toISOString(),

  selectedActionId: null,
  filterRisk: 'all',
  filterStatus: 'all',

  /* -- Agent lifecycle --------------------------------------------- */

  setPolicy: (policy) => {
    const permissions = computeEffectivePermissions(policy);
    set({
      policy,
      status: {
        ...get().status,
        tier: policy.tier,
        permissions,
        killSwitchEngaged: policy.killSwitch,
      },
    });
  },

  updatePolicy: (patch) => {
    const newPolicy = { ...get().policy, ...patch };
    get().setPolicy(newPolicy);
  },

  setTier: (tier) => {
    get().updatePolicy({ tier });
  },

  engageKillSwitch: () => {
    get().updatePolicy({ killSwitch: true });
    // Reject all pending actions
    const pending = get().pendingActions;
    for (const action of pending) {
      get().rejectAction(action.id, 'Kill switch engaged — all pending actions rejected');
    }
  },

  disengageKillSwitch: () => {
    get().updatePolicy({ killSwitch: false });
  },

  /* -- Permission management --------------------------------------- */

  getEffectivePermissions: () => {
    return computeEffectivePermissions(get().policy);
  },

  hasPermission: (permission) => {
    return get().getEffectivePermissions().includes(permission);
  },

  canExecuteAction: (actionType) => {
    const state = get();

    // Kill switch
    if (state.policy.killSwitch) {
      return { allowed: false, reason: 'Kill switch is engaged. All AI actions are blocked.' };
    }

    // Blocked actions
    if (state.policy.blockedActions.includes(actionType)) {
      return {
        allowed: false,
        reason: `Action "${actionType}" is explicitly blocked by org policy.`,
      };
    }

    // Rate limit
    if (state.actionsThisHour >= state.policy.maxActionsPerHour) {
      return {
        allowed: false,
        reason: `Rate limit exceeded (${state.policy.maxActionsPerHour}/hour).`,
      };
    }

    // Budget
    if (state.spentToday >= state.policy.maxDailyBudget) {
      return { allowed: false, reason: `Daily budget exceeded ($${state.policy.maxDailyBudget}).` };
    }

    // Mainnet deploy check
    if (actionType === 'ai.deploy.mainnet' && !state.policy.allowMainnetDeploy) {
      return { allowed: false, reason: 'Mainnet deployment is not allowed by org policy.' };
    }

    // Secret access check
    if (actionType === 'ai.security.rotate_keys' && !state.policy.allowSecretAccess) {
      return { allowed: false, reason: 'Secret/key access is not allowed by org policy.' };
    }

    // Permission check
    void ACTION_RISK_MAP[actionType]; // touches risk table; kept for future risk-gated permission logic
    const permMap: Partial<Record<AiActionType, AiPermission>> = {
      'ai.code.create': 'code:create',
      'ai.code.modify': 'code:modify',
      'ai.code.delete': 'code:delete',
      'ai.code.refactor': 'code:modify',
      'ai.build.compile': 'build:compile',
      'ai.build.test': 'build:test',
      'ai.build.fix_errors': 'ai:fix',
      'ai.deploy.testnet': 'deploy:testnet',
      'ai.deploy.mainnet': 'deploy:mainnet',
      'ai.deploy.verify': 'deploy:testnet',
      'ai.infra.scale_up': 'infra:scale',
      'ai.infra.scale_down': 'infra:scale',
      'ai.infra.configure_rpc': 'rpc:configure',
      'ai.security.audit': 'ai:audit',
      'ai.security.patch': 'ai:fix',
      'ai.security.rotate_keys': 'secret:rotate',
      'ai.optimize.gas': 'ai:optimize',
      'ai.optimize.cost': 'ai:optimize',
      'ai.optimize.performance': 'ai:optimize',
      'ai.workspace.create': 'workspace:create',
      'ai.workspace.configure': 'workspace:modify',
      'ai.workspace.cleanup': 'workspace:modify',
    };

    const required = permMap[actionType];
    if (required && !get().hasPermission(required)) {
      return { allowed: false, reason: `Missing permission: ${required}` };
    }

    return { allowed: true };
  },

  /* -- Action pipeline --------------------------------------------- */

  proposeAction: (actionInput) => {
    const state = get();
    const risk = ACTION_RISK_MAP[actionInput.type];
    const needsApproval = actionNeedsApproval(risk, state.policy, state.policy.tier);

    const action: AiAction = {
      ...actionInput,
      id: nextActionId(),
      risk,
      status: needsApproval ? 'proposed' : 'approved',
      autoApproved: !needsApproval,
      proposedAt: new Date().toISOString(),
      approvedAt: needsApproval ? undefined : new Date().toISOString(),
      expiresAt: new Date(Date.now() + 30 * 60_000).toISOString(), // 30 min expiry
    };

    if (needsApproval) {
      set((s) => ({
        pendingActions: [action, ...s.pendingActions],
      }));
    } else {
      // Auto-approved — go straight to executing queue
      set((s) => ({
        executingActions: [action, ...s.executingActions],
        actionsThisHour: s.actionsThisHour + 1,
        status: { ...s.status, actionsThisHour: s.actionsThisHour + 1 },
      }));
    }

    return action;
  },

  approveAction: (actionId, approvedBy, note) => {
    set((s) => {
      const action = s.pendingActions.find((a) => a.id === actionId);
      if (!action) return s;

      const approved: AiAction = {
        ...action,
        status: 'approved',
        approvedBy,
        approvalNote: note,
        approvedAt: new Date().toISOString(),
      };

      return {
        pendingActions: s.pendingActions.filter((a) => a.id !== actionId),
        executingActions: [approved, ...s.executingActions],
        actionsThisHour: s.actionsThisHour + 1,
        status: { ...s.status, actionsThisHour: s.actionsThisHour + 1 },
      };
    });
  },

  rejectAction: (actionId, note) => {
    set((s) => {
      const action = s.pendingActions.find((a) => a.id === actionId);
      if (!action) return s;

      const rejected: AiAction = {
        ...action,
        status: 'rejected',
        approvalNote: note,
        completedAt: new Date().toISOString(),
      };

      return {
        pendingActions: s.pendingActions.filter((a) => a.id !== actionId),
        actionHistory: [rejected, ...s.actionHistory].slice(0, 200),
      };
    });
  },

  startExecution: (actionId) => {
    set((s) => ({
      executingActions: s.executingActions.map((a) =>
        a.id === actionId
          ? { ...a, status: 'executing' as const, executedAt: new Date().toISOString() }
          : a,
      ),
      status: {
        ...s.status,
        currentAction: s.executingActions.find((a) => a.id === actionId) ?? null,
      },
    }));
  },

  completeAction: (actionId, result) => {
    set((s) => {
      const action = s.executingActions.find((a) => a.id === actionId);
      if (!action) return s;

      const completed: AiAction = {
        ...action,
        status: 'completed',
        result: result ?? undefined,
        completedAt: new Date().toISOString(),
      };

      return {
        executingActions: s.executingActions.filter((a) => a.id !== actionId),
        actionHistory: [completed, ...s.actionHistory].slice(0, 200),
        spentToday: s.spentToday + (action.estimatedCost?.usd ?? 0),
        status: {
          ...s.status,
          currentAction: null,
          spentToday: s.spentToday + (action.estimatedCost?.usd ?? 0),
        },
      };
    });
  },

  failAction: (actionId, errors) => {
    set((s) => {
      const action = s.executingActions.find((a) => a.id === actionId);
      if (!action) return s;

      const failed: AiAction = {
        ...action,
        status: 'failed',
        result: {
          success: false,
          summary: `Action failed: ${errors[0] ?? 'Unknown error'}`,
          outputs: {},
          errors,
          durationMs: action.executedAt ? Date.now() - new Date(action.executedAt).getTime() : 0,
        },
        completedAt: new Date().toISOString(),
      };

      return {
        executingActions: s.executingActions.filter((a) => a.id !== actionId),
        actionHistory: [failed, ...s.actionHistory].slice(0, 200),
        status: { ...s.status, currentAction: null },
      };
    });
  },

  rollbackAction: (actionId) => {
    set((s) => {
      const action = s.actionHistory.find(
        (a) => a.id === actionId && a.status === 'completed' && a.rollback?.canRollback,
      );
      if (!action) return s;

      const rolledBack: AiAction = {
        ...action,
        status: 'rolled_back',
        completedAt: new Date().toISOString(),
      };

      return {
        actionHistory: s.actionHistory.map((a) => (a.id === actionId ? rolledBack : a)),
      };
    });
  },

  expireAction: (actionId) => {
    set((s) => {
      const action = s.pendingActions.find((a) => a.id === actionId);
      if (!action) return s;

      const expired: AiAction = {
        ...action,
        status: 'expired',
        completedAt: new Date().toISOString(),
      };

      return {
        pendingActions: s.pendingActions.filter((a) => a.id !== actionId),
        actionHistory: [expired, ...s.actionHistory].slice(0, 200),
      };
    });
  },

  /* -- UI ---------------------------------------------------------- */

  selectAction: (id) => set({ selectedActionId: id }),
  setFilterRisk: (risk) => set({ filterRisk: risk }),
  setFilterStatus: (status) => set({ filterStatus: status }),

  /* -- Computed ---------------------------------------------------- */

  getPendingCount: () => get().pendingActions.length,

  getActionById: (id) => {
    const s = get();
    return (
      s.pendingActions.find((a) => a.id === id) ??
      s.executingActions.find((a) => a.id === id) ??
      s.actionHistory.find((a) => a.id === id)
    );
  },

  isRateLimited: () => {
    const s = get();
    return s.actionsThisHour >= s.policy.maxActionsPerHour;
  },

  isBudgetExceeded: () => {
    const s = get();
    return s.spentToday >= s.policy.maxDailyBudget;
  },
}));
